<script>
	import { tick } from "svelte";
	import { isValid } from "../../validation";
	import model from "../../stores/modelStore";
	import { acceptedFileUploadListSpreadsheet, projectSettings } from "../../config";
	import { validator } from "../../validation";
	import { registerPageComplete } from "../../gtm";

	import Holding from "../../models/Holding";
	import HoldingTransaction from "../../models/HoldingTransaction";

	import PageSection from "../../components/PageSection.svelte";
	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";
	import PrimaryButton from "../../components/buttons/PrimaryButton.svelte";
	import AddButton from "../../components/buttons/AddButton.svelte";
	import DeleteButton from "../../components/buttons/DeleteButton.svelte";
	import HoldingView from "./components/HoldingView.svelte";
	import HoldingEdit from "./components/HoldingEdit.svelte";
	import { getHoldingRequiredFieldErrors } from "./components/holdingValidation";

	// props
	export let disabled = null;
	export let pageName = null;
	export let pageTitle = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;

	let buttons = null;
	let showHoldingValidationErrors = false;

	// dropzone config
	let id = "fileUploader";
	let options = {
		url: "/api/file-upload?token=" + $model.RegistrationToken,
		acceptedFiles: acceptedFileUploadListSpreadsheet
	};

	// component state updating
	if (!disabled) {
		$model.LastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	// list management
	if ($model.Holdings.length === 0) {
		$model.Holdings = [new Holding()];
	}

	let liveIndex = 0;
	$: liveHolding = $model.Holdings[liveIndex];

	const addHolding = () => {
		if ($validator.validateAll(document.querySelector("form")).length > 0) return;

		$model.Holdings = [...$model.Holdings, new Holding()];
		liveIndex = $model.Holdings.length - 1;
	};

	const removeHolding = (index) => {
		if (!confirm("Confirm delete holding")) return;

		if ($model.Holdings.length <= 1) return;
		if (liveIndex === index) liveIndex = 0;
		else if (liveIndex > index) liveIndex -= 1;
		$model.Holdings = $model.Holdings.filter((_, i) => i !== index);
	};

	const setLiveHolding = (index) => {
		if ($validator.validateAll(document.querySelector("form")).length > 0) return;
		liveIndex = index;
	};

	const hasPriceErrors = () =>
		$model.Holdings.some((holding) =>
			holding.HoldingTransactions?.some((transaction) => transaction.hasPriceError)
		);
	const getRequiredFieldErrors = () =>
		$model.Holdings.flatMap((holding) => getHoldingRequiredFieldErrors(holding));

	// update model on name input binding
	$: if (liveHolding.HoldingName) {
		liveHolding = $model.Holdings[liveIndex];
	}

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();
		let requiredFieldErrors = getRequiredFieldErrors();
		let hasPriceError = hasPriceErrors();
		showHoldingValidationErrors = requiredFieldErrors.length > 0 || hasPriceError;

		if (!errors.length && !requiredFieldErrors.length && !hasPriceError) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
			} else {
				await registerPageComplete(pageName);

				buttons.next();
			}
		} else {
			let firstError = errors[0];
			if (!firstError && (requiredFieldErrors.length || hasPriceError)) {
				await tick();
				firstError = Array.from(document.querySelectorAll(".holding-validation-errors")).find(
					(element) => element.getClientRects().length > 0
				);
			}

			firstError?.scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};
</script>

<PageSection>
	<Form {disabled} id="form-holding-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Holding and Transactions</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn>
					<p>
						Please upload your share transaction history (buys and sells) for the period 15 August 2025 and 21 April 2026 (inclusive) in CSV or Excel format. 
						Instructions for downloading your trade file from some common broker platforms are provided below.
					</p>
					<p>
						Alternatively, you can also input your share transaction details manually by clicking on the <strong>'Add transactions manually'</strong> button.
					</p>
					<p>
						The following sections must be completed per holding. 
						If you wish to submit multiple holdings, please select <strong>"Add Holding"</strong>.
					</p>
				</FormColumn>
			</FormRow>

			{#if !disabled}
				<FormRow>
					<FormColumn>
						{#each $model.Holdings as holding, i (holding.Id)}
							<div class="holding-panel" class:active={liveHolding && holding.Id === liveHolding.Id}>
								<div class="button-panel">
									<h2>Holding {i + 1}</h2>
									<div>
										{#if holding.Id !== liveHolding.Id}
											<PrimaryButton on:click={() => setLiveHolding(i)}>Edit</PrimaryButton>
										{/if}
										{#if $model.Holdings.length > 1}
											<DeleteButton on:delete={() => removeHolding(i)}></DeleteButton>
										{/if}
									</div>
								</div>

								{#if !disabled && liveHolding && holding.Id === liveHolding.Id}
									<HoldingEdit bind:holding registrationToken={$model.RegistrationToken} />
								{:else}
									<HoldingView
										bind:holding
										registrationToken={$model.RegistrationToken}
										showValidationErrors={showHoldingValidationErrors} />
								{/if}
							</div>
						{/each}
					</FormColumn>
				</FormRow>
				<FormRow class="align-items-end mt-2">
					<FormColumn><AddButton on:add={addHolding}>Add Holding</AddButton></FormColumn>
				</FormRow>
			{:else}
				{#each $model.Holdings as holding, i (holding.Id)}
					<h2>Holding {i + 1}</h2>
					<div class="holding-panel">
						<HoldingView bind:holding />
					</div>
				{/each}
			{/if}
		</div>
		<FormRow class="confirmation-hide">
			<FormColumn class="last">
				<NavigationButtons bind:buttons nextPageLabel="Next" />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>

<style>
	:global(.transaction .form-control) {
		height: 40px;
		line-height: 14.5px;
		font-size: 14px;
	}

	:global(.transaction .currency-group .symbol) {
		line-height: 40px !important;
	}

	:global(.transaction .datepicker .calendar-pop) {
		line-height: 40px;
	}

	.holding-panel {
		/* display: flex;
		justify-content: space-between; */
		align-items: flex-start;
		padding: 1rem;
		border: 1px solid var(--color-border);
		background-color: #fff;
		border-radius: 0.25rem;
		margin-bottom: 1rem;
		transition:
			border-color 0.15s ease-in-out,
			box-shadow 0.15s ease-in-out;
	}

	.holding-panel.active {
		border: none;
		box-shadow: 0 0 0 0.25rem rgba(255, 72, 20, 0.25);
	}

	.button-panel {
		display: flex;
		justify-content: space-between;
		gap: 0.5rem;
		flex-shrink: 0;
	}
</style>
